import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Header } from './Header';
import { StatusBar } from './StatusBar';
import { SettingsSidebar } from './SettingsSidebar';
import { FilterIndicatorBanner } from '@/components/settings/FilterIndicatorBanner';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { useFilters } from '@/contexts/FilterContext';
import { SettingsFilterProvider } from '@/contexts/SettingsFilterProvider';
import { useUnitStatus } from '@/contexts/UnitStatusContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { getUnits } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { Unit, UnitFilterData } from '@/types';
import type { AlertFilterState } from '@/lib/unitFilters';
import { LottieLoading } from '@/components/ui/lottie-loading';

export function SettingsLayout() {
  const { permissions, loading: settingsLoading } = useSettingsContext();
  const { selectedCompanyIds, selectedGroupIds, selectedIndustryTypes, selectedModels, companies, groups, setSelectedCompanyIds, setSelectedGroupIds, setUnits: setContextUnits, units: contextUnits } = useFilters();
  const { getStatus } = useUnitStatus();
  const isMobile = useIsMobile();

  // State for StatusBar - accepts either full Unit or minimal UnitFilterData
  const [units, setUnits] = useState<(Unit | UnitFilterData)[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState<Set<'online' | 'offline' | 'idle' | 'unknown'>>(
    new Set(['online', 'offline', 'idle', 'unknown'])
  );
  const [showAlerts, setShowAlerts] = useState<AlertFilterState>('none');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Persist sidebar collapsed state in localStorage
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('settings_sidebar_collapsed') === 'true';
  });

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('settings_sidebar_collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Track if we've already loaded to prevent double-loading
  const hasLoadedRef = useRef(false);

  // Load units for unit snapshot and push to FilterContext
  const loadUnits = useCallback(async () => {
    try {
      const data = await getUnits();
      setUnits(data);
      // Push to FilterContext so filter dropdowns have accurate data
      setContextUnits(data);
    } catch (err) {
      console.error('Failed to load units for settings:', err);
    }
  }, [setContextUnits]);

  useEffect(() => {
    // Only load once, and only if we don't have units in context yet
    if (hasLoadedRef.current) {
      // Still sync local state with context when context changes
      if (contextUnits.length > 0) {
        setUnits(contextUnits);
      }
      return;
    }

    if (contextUnits.length === 0) {
      hasLoadedRef.current = true;
      loadUnits();
    } else {
      // Use context units if already loaded (e.g., from Units page)
      hasLoadedRef.current = true;
      setUnits(contextUnits);
    }
  }, [contextUnits, loadUnits]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadUnits();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilters(new Set(['online', 'offline', 'idle', 'unknown']));
    setShowAlerts('none');
    setSelectedCompanyIds(companies.map((a) => a.id));
    setSelectedGroupIds(groups.map(g => g.id));
  };

  // Calculate snapshot counts based on units filtered by Company/Group/Type/Model ONLY
  // This provides a "Unit Snapshot" regardless of the transient status/alert filters
  const snapshotUnits = useMemo(() => {
    return units.filter((unit: Unit | UnitFilterData) => {
      // Filter by selected industry types
      const unitType = unit.industry_type || 'film';
      const matchesType = selectedIndustryTypes.includes(unitType);

      // Filter by selected models
      const modelPrefix = unit.unit_code.substring(0, 3);
      const matchesModel = selectedModels.includes(modelPrefix);

      // Filter by selected companies
      let matchesCompany = selectedCompanyIds.length === 0;
      if (!matchesCompany) {
        matchesCompany = selectedCompanyIds.includes(unit.company_id) ||
                        (selectedCompanyIds.includes('__unassigned__') && !unit.company_id);
      }

      // Filter by selected groups
      let matchesGroup = selectedGroupIds.length === 0;
      if (!matchesGroup) {
        matchesGroup = (unit.primary_group_id && selectedGroupIds.includes(unit.primary_group_id)) ||
                      (selectedGroupIds.includes(`__unassigned_${unit.company_id}__`) && !unit.primary_group_id);
      }

      return matchesType && matchesModel && matchesCompany && matchesGroup;
    });
  }, [units, selectedIndustryTypes, selectedModels, selectedCompanyIds, selectedGroupIds]);

  // Calculate counts from the snapshot using live status from UnitStatusContext
  // This ensures accurate counts even when units have placeholder status values
  const onlineCount = snapshotUnits.filter((t: Unit | UnitFilterData) => getStatus(t.id) === 'online').length;
  const offlineCount = snapshotUnits.filter((t: Unit | UnitFilterData) => getStatus(t.id) === 'offline').length;
  const idleCount = snapshotUnits.filter((t: Unit | UnitFilterData) => getStatus(t.id) === 'idle').length;
  const warningCount = snapshotUnits.filter((t: Unit | UnitFilterData) => t.alerts && t.alerts > 0 && t.alertType === 'warning').length;
  const faultCount = snapshotUnits.filter((t: Unit | UnitFilterData) => t.alerts && t.alerts > 0 && t.alertType === 'fault').length;

  if (settingsLoading) {
    return <LottieLoading message="Loading settings..." />;
  }

  return (
    <SettingsFilterProvider>
      <div className="h-dvh flex flex-col overflow-hidden bg-background">
        {/* Top Header & Status Bar - Fixed at top */}
        <div className="flex-none z-40">
          <Header />
          <StatusBar
            onlineCount={onlineCount}
            offlineCount={offlineCount}
            idleCount={idleCount}
            warningCount={warningCount}
            faultCount={faultCount}
            onClearFilters={handleClearFilters}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            statusFilters={statusFilters}
            onStatusFiltersChange={setStatusFilters}
            showAlerts={showAlerts}
            onShowAlertsChange={setShowAlerts}
            units={units}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchOpen={isSearchOpen}
            onSearchOpenChange={setIsSearchOpen}
          />
          {/* Filter Indicator Banner - Shows when Settings filters are active */}
          <FilterIndicatorBanner />
        </div>

        {/* Content Area with Sidebar */}
        <div className="flex flex-1 overflow-hidden relative">
          {/* Desktop: Show sidebar, Mobile: Hide (use hamburger menu instead) */}
          {!isMobile && (
            <SettingsSidebar
              permissions={permissions}
              collapsed={isSidebarCollapsed}
              onToggle={setIsSidebarCollapsed}
            />
          )}

          {/* Main Scrollable Content */}
          <main
            className={cn(
              "flex-1 overflow-y-auto transition-all duration-300",
              isMobile ? "p-4" : "p-8",
              isMobile ? "ml-0" : (isSidebarCollapsed ? "ml-[60px]" : "ml-[240px] lg:ml-[360px]")
            )}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </SettingsFilterProvider>
  );
}
